import { onBeforeUnmount, ref, shallowRef } from 'vue';

import { wrapperRaf } from '@components/utils';

import type { Ref, UnwrapRef } from 'vue';

type StateUpdater<State> = (prev: State) => State;
export function useLayoutState<State>(defaultState: State): [Ref<State>, (updater: StateUpdater<State>) => void] {
  const stateRef = shallowRef<State>(defaultState);
  const updateBatchRef = shallowRef<StateUpdater<State>[]>([]);

  let rafId: number;
  function setFrameState(updater: StateUpdater<State>) {
    updateBatchRef.value.push(updater);

    wrapperRaf.cancel(rafId);

    rafId = wrapperRaf(() => {
      const prevBatch = updateBatchRef.value;

      updateBatchRef.value = [];

      prevBatch.forEach((batchUpdater) => {
        stateRef.value = batchUpdater(stateRef.value as State);
      });
    });
  }

  onBeforeUnmount(() => {
    wrapperRaf.cancel(rafId);
  });

  return [stateRef as Ref<State>, setFrameState];
}

/** Lock frame, when frame pass reset the lock. */
export function useTimeoutLock<State>(
  defaultState?: State
): [(state: UnwrapRef<State>) => void, () => UnwrapRef<State> | null] {
  const frameRef = ref<State | null>(defaultState || null);
  const timeoutRef = ref<any>();

  function cleanUp() {
    clearTimeout(timeoutRef.value);
  }

  function setState(newState: UnwrapRef<State>) {
    frameRef.value = newState;
    cleanUp();

    timeoutRef.value = setTimeout(() => {
      frameRef.value = null;
      timeoutRef.value = undefined;
    }, 100);
  }

  function getState() {
    return frameRef.value;
  }

  onBeforeUnmount(() => {
    cleanUp();
  });

  return [setState, getState];
}
